import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {Subscription} from "rxjs";
import {CommonService} from "../../../../services/common.service";

@Component({
  selector: 'app-blood-pressure-input',
  templateUrl: './blood-pressure-input.component.html',
  styleUrls: ['../../styles.css', './blood-pressure-input.component.css']
})
export class BloodPressureInputComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('input') input: ElementRef | undefined;

  @Input() label: string = '';
  @Input() focus: boolean = false;
  @Input() placeholder: string = '';
  @Input() parentFormGroup: FormGroup = new FormGroup<any>({});
  @Input() controlName: string = '';
  @Input() error: string = '';
  @Input() type: string = '';
  @Output() checkForErrors = new EventEmitter();

  fieldSubscription: Subscription | undefined;

  isRequired: boolean = false;
  isDisabled: boolean = false;

  form = new FormGroup({
    sys: new FormControl(''),
    dia: new FormControl(''),
  })
  formPlaceholders = {
    sys: "sys",
    dia: 'dia',
  }

  skipValueChange = false;

  valueChangesSubscription: Subscription;
  showWarning = false;

  constructor(
    private commonService: CommonService
  ) {
    this.valueChangesSubscription = this.form.valueChanges.subscribe((values) => {
      if (this.skipValueChange) {
        this.skipValueChange = false;
        return;
      }
      const parentFormControl = this.parentFormGroup.controls[this.controlName];

      const finalValueInInches = `${values.sys}/${values.dia}`;

      this.skipValueChange =  true;
      if (finalValueInInches) {
        parentFormControl.patchValue('' + finalValueInInches);
      } else {
        parentFormControl.patchValue('');
      }
      parentFormControl.markAsTouched();
      parentFormControl.markAsDirty();

      // show warning
      const sys = +values.sys;
      const dia = +values.dia;
      if (sys && dia) {
        if (sys > 140 || sys < 80) {
          this.showWarning = true;
        } else if (dia > 90 || dia < 40) {
          this.showWarning = true;
        } else if (sys <= dia) {
          this.showWarning = true;
        } else {
          this.showWarning = false;
        }
      } else {
        this.showWarning = false;
      }
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.['focus']?.currentValue) {
      const clientArea = $('#client_area');
      const y = clientArea.scrollTop() +
        this.input?.nativeElement.getBoundingClientRect().top -
        this.commonService.headerRemOffsetCalculator()

      clientArea.scrollTop(y);
    }
  }

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout', ['$event'])
  onBlur(event: Event) {
    if (!this.error) {
      this.parentFormGroup.controls[this.controlName].markAsUntouched();
    }
  }

  ngOnInit(): void {
    const control = this.parentFormGroup.controls[this.controlName];
    this.isDisabled = control.status === 'DISABLED';

    // set initial value to inputs
    this.setValueFromParentControl(control.value);

    this.fieldSubscription = control.valueChanges.subscribe((value) => {
      // set value to inputs
      this.setValueFromParentControl(value);

      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup.controls[this.controlName].touched) {
        this.parentFormGroup.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup.controls[this.controlName].errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control.validator) {
      const validator = control.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  setValueFromParentControl(value:string) {
    if (this.skipValueChange) {
      this.skipValueChange = false;
      return
    }
    if (value?.trim()) {
      let parsedValue = value.split('/');
      if (parsedValue?.length === 2) {
        this.skipValueChange = true;
        this.form.patchValue({
          sys: parsedValue[0],
          dia: parsedValue[1],
        });
      }
    }
  }

  ngOnDestroy() {
    this.valueChangesSubscription?.unsubscribe();
    this.fieldSubscription?.unsubscribe();
  }
}
